import { alpha, createTheme } from "@mui/material/styles";
import { DEFAULT_APPEARANCE, getAppearanceTokens } from "./appearanceTokens";

export const getAppTheme = (mode, direction = "ltr", preferences = DEFAULT_APPEARANCE) => {
  const appearance = getAppearanceTokens(preferences, mode);

  const colors = {
    primary: appearance.colors.primary,
    primaryDark: appearance.colors.primaryHover,
    background: appearance.colors.background,
    paper: appearance.colors.surface,
    text: appearance.colors.foreground,
    muted: appearance.colors.mutedForeground,
    border: appearance.colors.border,
  };

  return createTheme({
    direction,

    typography: {
      fontFamily: 'var(--font-family, "Segoe UI", Arial, sans-serif)',
    },

    palette: {
      mode,

      primary: {
        main: colors.primary,
        dark: colors.primaryDark,
        contrastText: appearance.colors.primaryForeground,
      },

      secondary: {
        main: colors.muted,
      },

      background: {
        default: colors.background,
        paper: colors.paper,
      },

      text: {
        primary: colors.text,
        secondary: colors.muted,
      },

      error: {
        main: appearance.vars["--error"],
      },

      warning: {
        main: appearance.vars["--warning"],
      },

      success: {
        main: appearance.vars["--success"],
      },

      divider: colors.border,
    },

    shape: { borderRadius: appearance.radius },

    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: colors.paper,
            border: `1px solid ${colors.border}`,
            boxShadow: "var(--section-shadow, 0 4px 16px rgba(16, 24, 40, .08))",
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: appearance.vars["--surface-muted"],
            minHeight: appearance.density.control,
            borderRadius: appearance.radius,
            boxShadow: "none",
            transition: "background-color 160ms ease, border-color 160ms ease",

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.border,
              borderWidth: "1px",
              transition: "border-color 160ms ease",
            },

            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(colors.primary, 0.55),
            },

            "&.Mui-focused": {
              backgroundColor: alpha(colors.primary, mode === "dark" ? 0.09 : 0.035),
              boxShadow: "none",
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary,
              borderWidth: "1px",
            },

            "&.Mui-disabled": {
              backgroundColor: alpha(colors.muted, mode === "dark" ? 0.4 : 0.65),
            },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: colors.muted,
            transition: "color 160ms ease, transform 160ms ease",

            "&.Mui-focused": {
              color: colors.primary,
              fontWeight: 600,
            },
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: appearance.density.control,
            borderRadius: appearance.radius,
            fontWeight: 650,
            textTransform: "none",
          },
          containedPrimary: {
            backgroundColor: colors.primary,
            color: appearance.colors.primaryForeground,
            borderRadius: appearance.radius,
            fontWeight: 650,

            "&:hover": {
              backgroundColor: colors.primaryDark,
            },
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: `${appearance.vars["--primary-muted"]} !important`,
              color: colors.primary,
            },

            "&:hover": {
              backgroundColor: appearance.vars["--surface-hover"],
            },
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.paper,
            color: colors.text,
            boxShadow: "none",
            borderBottom: `1px solid ${colors.border}`,
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: colors.paper,
            boxShadow: "var(--section-shadow, 0 4px 16px rgba(16, 24, 40, .08))",
            border: `1px solid ${colors.border}`,
            borderRadius: appearance.radius,
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: colors.border,
            paddingTop: Math.max(6, (appearance.density.row - 32) / 2),
            paddingBottom: Math.max(6, (appearance.density.row - 32) / 2),
          },
          head: { color: colors.muted, fontWeight: 700, whiteSpace: "nowrap" },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: { border: `1px solid ${colors.border}`, backgroundImage: "none" },
        },
      },
    },
  });
};
